/**
 * useLanguage — syncs the user's profile language preference with i18next.
 *
 * Called once on app mount (inside App.tsx after auth resolves).
 * Reads profiles.language from Supabase and calls i18n.changeLanguage()
 * so all t() calls in the app use the user's stored preference.
 *
 * Also exports setLanguage() for use in the Profile settings selector.
 */
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { db } from '../lib/supabase'

export type SupportedLanguage = 'en' | 'pl'

export const LANGUAGE_LABELS: Record<SupportedLanguage, string> = {
  en: 'English',
  pl: 'Polski',
}

export function useLanguage() {
  const { i18n } = useTranslation()

  /**
   * Apply the seeker's stored language, if they have chosen one.
   *
   * `profiles.language` is nullable and NULL means "never chose" (#35). A
   * missing row means the same thing: the row is not created until the END of
   * onboarding, so every brand-new seeker spends their whole first session
   * without one. In both cases we must leave the detected language alone.
   *
   * This used to fall back to 'en', which switched a Polish seeker arriving on
   * a `?lang=pl` link into English before the first onboarding screen and then
   * cached that in localStorage, so the next visit was English too. Never write
   * `ia_language` for a language the seeker did not pick.
   */
  const loadLanguage = useCallback(async (userId: string) => {
    const { data } = await db()
      .from('profiles')
      .select('language')
      .eq('id', userId)
      .maybeSingle()

    const chosen = (data as { language?: string | null } | null)?.language ?? null
    if (!chosen) return

    if (chosen !== i18n.language) {
      await i18n.changeLanguage(chosen)
      localStorage.setItem('ia_language', chosen)
    }
  }, [i18n])

  /** Save language to profile and apply it immediately. */
  const setLanguage = useCallback(async (userId: string, lang: SupportedLanguage) => {
    await i18n.changeLanguage(lang)
    localStorage.setItem('ia_language', lang)
    await db()
      .from('profiles')
      .update({ language: lang })
      .eq('id', userId)
  }, [i18n])

  return {
    currentLanguage: i18n.language as SupportedLanguage,
    loadLanguage,
    setLanguage,
  }
}
